import React, { useCallback } from "react";
import Pie from "@visx/shape/lib/shapes/Pie";
import { Group } from "@visx/group";
import { scaleOrdinal } from "@visx/scale";
import { Text } from "@visx/text";
import classNames from "classnames";
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';

import { PieSlice } from "../pie-slice";
import { defaultPieChartColorRange, defaultPieChartMargin } from "../utils";
import { PieChartSvgProps } from "../types";
import { Box, useTheme } from "@mui/material";
import { getChartTooltipStyles, getChartTooltipSwatchStyle } from "../../../utils/chart-styles";

interface TooltipData {
  label: string;
  value: number;
  color: string;
}

export const PieChartSvg = <DataType,>({
  data,
  width,
  height,
  margin = defaultPieChartMargin,
  animate = true,
  getId,
  getValue,
  getLabel,
  getColor,
  title,
  textColor,
  className,
  onSelect,
  selection,
  strokeColor,
}: PieChartSvgProps<DataType>) => {
  const theme = useTheme();
  const tooltipStyles = getChartTooltipStyles(theme);
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
  } = useTooltip<TooltipData>();

  const getDataColor = scaleOrdinal({
    domain: data.map((sliceData) => getId(sliceData)),
    range: defaultPieChartColorRange,
  });

  // Handle slice hover
  const handleSliceHover = useCallback(
    (event: React.MouseEvent<SVGPathElement>, sliceData: DataType) => {
      const point = localPoint(event);
      if (!point) return;
      const rawLabel = (getLabel && getLabel(sliceData)) || getId(sliceData);
      showTooltip({
        tooltipData: {
          label: String(rawLabel),
          value: getValue(sliceData),
          color: (getColor && getColor(sliceData)) || getDataColor(getId(sliceData)),
        },
        tooltipLeft: point.x,
        tooltipTop: point.y,
      });
    },
    [getId, getValue, getLabel, getColor, getDataColor, showTooltip]
  );

  if (width < 10) return null;

  const chartMargin = {
    ...defaultPieChartMargin,
    ...margin,
  };
  // Add extra bottom padding for labels that extend below the pie
  const labelPadding = 24;
  const innerWidth = width - chartMargin.left - chartMargin.right;
  const innerHeight = height - chartMargin.top - chartMargin.bottom - labelPadding;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const svgPadding = 28;

  return (
    <Box
      className={classNames("pie-chart-container", className)}
      sx={{
        width: innerWidth,
        mx: "auto",
        overflow: "visible",
        position: "relative",
      }}
    >
      <svg width={innerWidth} height={height} overflow={"visible"}>
        <Group
          top={centerY + chartMargin.top}
          left={centerX + chartMargin.left}
        >
          <Pie
            data={
              selection
                ? data.filter(
                    (sliceData) => getId(sliceData) === getId(selection)
                  )
                : data
            }
            pieValue={getValue}
            pieSortValues={() => -1}
            outerRadius={radius - svgPadding}
          >
            {(pie) => (
              <PieSlice<DataType>
                {...pie}
                textColor={textColor}
                animate={animate}
                getKey={({ data }) => getId(data)}
                onClickDatum={({ data }) =>
                  animate &&
                  onSelect &&
                  onSelect(
                    selection && getId(selection) === getId(data) ? null : data
                  )
                }
                getColor={({ data }) =>
                  (getColor && getColor(data)) ?? getDataColor(getId(data))
                }
                getLabel={(d) => String((getLabel && getLabel(d)) || getId(d))}
                strokeColor={strokeColor}
                onMouseMoveDatum={(event, { data }) => handleSliceHover(event, data)}
                onMouseLeaveDatum={hideTooltip}
              />
            )}
          </Pie>
        </Group>
        {title && (
          <Text
            x={centerX}
            y={centerY - width / 2}
            textAnchor="middle"
            fill={textColor ?? "#000"}
            fontSize={32}
          >
            {title}
          </Text>
        )}
      </svg>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          style={tooltipStyles}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {tooltipData.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={getChartTooltipSwatchStyle(tooltipData.color)} />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>{tooltipData.value.toLocaleString()}</span>
          </div>
        </TooltipWithBounds>
      )}
    </Box>
  );
};
